import React from 'react';
import { Box, Button, CircularProgress, Divider, Typography, Chip, Paper, Stack, IconButton, Tooltip } from '@mui/material';
import { Delete } from '@mui/icons-material';
import { retornaDataBr } from '../../../../utils/retornaDataFormatada';

function getStatusPrazo(item) {
    const hoje = new Date();
    const dataFim = item?.dataFim ? new Date(item.dataFim) : null;
    const dataHomologado = item?.dataHomologado ? new Date(item.dataHomologado) : null;

    if (!dataFim) {
        return { label: 'Sem prazo', color: 'default' };
    }

    if (dataHomologado) {
        if (dataHomologado <= dataFim) {
            return { label: 'Homologado no prazo', color: 'success' };
        }
        return { label: 'Homologado com atraso', color: 'warning' };
    }

    if (hoje <= dataFim) {
        return { label: 'Em prazo', color: 'info' };
    }

    return { label: 'Atrasado', color: 'error' };
}

function CardResumo({ label, valor }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.2,
                minWidth: 170,
                flex: '1 1 170px',
                borderRadius: 1.5,
            }}
        >
            <Typography variant="caption" sx={{ fontWeight: 'bold', display: 'block', mb: 0.5 }}>
                {label}
            </Typography>
            <Typography variant="body2">{valor || '-'}</Typography>
        </Paper>
    );
}

function LinhaPessoa({ row, prazoVenceu, onRemover }) {
    let statusLabel = 'Pendente';
    let statusColor = 'warning';

    if (row?.resolveu) {
        statusLabel = 'Concluído';
        statusColor = 'success';
    } else if (prazoVenceu) {
        statusLabel = 'Atrasado';
        statusColor = 'error';
    }

    return (
        <Box
            sx={{
                py: 1,
                px: 1,
                borderBottom: '1px solid',
                borderColor: 'divider',
            }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} useFlexGap flexWrap="wrap">
                <Typography>
                    <Tooltip
                        title={
                            row?.podeRemover
                                ? 'Remover pessoa deste item'
                                : 'Não é possível remover: já possui registros ou já saiu do item'
                        }
                    >
                        <span>
                            <IconButton
                                size="small"
                                color="error"
                                disabled={!row?.podeRemover}
                                onClick={() => {
                                    if (!row?.podeRemover) return;
                                    onRemover();
                                }}
                            >
                                <Delete fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                </Typography>
                <Typography
                    variant="body2"
                    noWrap
                    sx={{
                        minWidth: 350,
                        maxWidth: 350,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                    title={row?.Pessoa?.descricao}
                >
                    {row?.Pessoa?.descricao || '-'}
                </Typography>

                <Typography variant="body2">
                    <strong>Papel:</strong> {row?.Papel?.descricao || '-'}
                </Typography>

                <Chip label={statusLabel} color={statusColor} size="small" />

                <Typography variant="body2">
                    <strong>Entrada:</strong> {row?.dataEntrada ? retornaDataBr(row.dataEntrada, 'date') : '-'}
                </Typography>

                <Typography variant="body2">
                    <strong>Saída:</strong> {row?.dataSaida ? retornaDataBr(row.dataSaida, 'date') : '-'}
                </Typography>
            </Stack>

            {row?.observacao ? (
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                    <strong>Obs:</strong> {row.observacao}
                </Typography>
            ) : null}
        </Box>
    );
}

function BlocoVazio({ text }) {
    return (
        <Paper
            variant="outlined"
            sx={{
                p: 1.5,
                borderStyle: 'dashed',
                color: 'text.secondary',
            }}
        >
            <Typography variant="body2">{text}</Typography>
        </Paper>
    );
}

function HomologationItemDetailPanel({
    item,
    detail,
    loading,
    pessoaId,
    isResponsavelHomologacao,
    onAssinar,
    onRemoverAssinatura,
    onOpenRemoverPessoaDialog,
}) {
    const pessoas = detail?.pessoas || [];
    const registros = detail?.registros || [];
    const assinaturas = detail?.assinaturas || [];
    const statusPrazo = getStatusPrazo(item);

    const usuariosResolvidos = new Set();

    registros.forEach((registro) => {
        if (registro?.concluiRegistro) {
            usuariosResolvidos.add(registro.createdUser);
        }
    });

    const participantes = pessoas.map((pessoa) => {
        const pessoaIdRow = pessoa?.pessoaId;

        const registrosDaPessoa = registros.filter((registro) => String(registro?.createdUser) === String(pessoaIdRow));

        const resolveu = registrosDaPessoa.some((registro) => registro?.concluiRegistro === true);

        return {
            ...pessoa,
            resolveu,
            totalRegistros: registrosDaPessoa.length,
            podeRemover: !pessoa?.dataSaida && registrosDaPessoa.length === 0 && !!pessoa?.itemId,
        };
    });

    const minhaAssinatura = assinaturas.find((a) => String(a?.pessoaId) === String(pessoaId) && a?.ativo !== false);

    const meusRegistrosResolvidos = registros.filter(
        (r) => String(r?.createdUser) === String(pessoaId) && r?.concluiRegistro === true
    );

    const possoAssinar = !minhaAssinatura && (isResponsavelHomologacao || meusRegistrosResolvidos.length > 0);

    const possoRemoverAssinatura =
        !!minhaAssinatura && (String(minhaAssinatura?.pessoaId) === String(pessoaId) || isResponsavelHomologacao);

    const pendentes = participantes.filter((p) => !p.resolveu);

    const prazoVenceu = !!item?.dataFim && new Date() > new Date(item.dataFim);

    const atrasados = prazoVenceu ? pendentes : [];

    const progresso =
        participantes.length === 0
            ? 0
            : Math.round((participantes.filter((p) => p.resolveu).length / participantes.length) * 100);

    if (loading) {
        return (
            <Box
                sx={{
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    backgroundColor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1.5,
                }}
            >
                <CircularProgress size={24} />
                <Typography variant="body2">Carregando detalhes do item...</Typography>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                p: 2,
                backgroundColor: 'background.paper',
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1.5,
            }}
        >
            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1}
                sx={{ mb: 2 }}
            >
                <Box>
                    <Typography variant="h6">Resumo do Item</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {item?.descricao || item?.menuSub?.descricao || 'Item da homologação'}
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip label={statusPrazo.label} color={statusPrazo.color} size="small" />
                    <Chip label={`${participantes.length} pessoa(s)`} variant="outlined" size="small" />
                    <Chip label={`${pendentes.length} pendente(s)`} color="warning" size="small" />
                    <Chip label={`${atrasados.length} atrasado(s)`} color="error" size="small" />
                    <Chip label={`Progresso ${progresso}%`} color="success" size="small" />
                    <Chip label={`${registros.length} registro(s)`} variant="outlined" size="small" />
                </Stack>
            </Stack>

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2 }}>
                <CardResumo label="Situação" valor={item?.Situacao?.descricao} />
                <CardResumo label="Operação" valor={item?.Operacao?.descricao} />
                <CardResumo label="Prazo Final" valor={item?.dataFim ? retornaDataBr(item.dataFim, 'date') : '-'} />
                <CardResumo
                    label="Data Homologado"
                    valor={item?.dataHomologado ? retornaDataBr(item.dataHomologado, 'date') : '-'}
                />
                <CardResumo label="Versão Front" valor={item?.versaoFrontHomologada} />
                <CardResumo label="Versão Back" valor={item?.versaoBackHomologada} />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Pessoas do Item
                </Typography>
                <Chip label={`${pessoas.length} vínculo(s)`} size="small" variant="outlined" />
            </Stack>

            {pessoas.length ? (
                <Paper variant="outlined" sx={{ borderRadius: 1.5 }}>
                    {participantes.map((row) => (
                        <LinhaPessoa
                            key={row.uuid || `${row.itemId}-${row.pessoaId}`}
                            row={row}
                            prazoVenceu={prazoVenceu}
                            onRemover={() => onOpenRemoverPessoaDialog(row)}
                        />
                    ))}
                </Paper>
            ) : (
                <BlocoVazio text="Nenhuma pessoa vinculada a este item." />
            )}

            <Divider sx={{ my: 2 }} />

            <Stack
                direction={{ xs: 'column', md: 'row' }}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', md: 'center' }}
                spacing={1}
                sx={{ mb: 1 }}
            >
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                    Assinatura do Item
                </Typography>
            </Stack>

            <Paper variant="outlined" sx={{ borderRadius: 1.5, p: 1.5 }}>
                {minhaAssinatura ? (
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'flex-start', md: 'center' }}>
                        <Chip label="Assinado" color="success" size="small" />
                        <Typography variant="body2">
                            Assinado em: {minhaAssinatura?.createdAt ? retornaDataBr(minhaAssinatura.createdAt, 'date') : '-'}
                        </Typography>
                        {possoRemoverAssinatura && (
                            <Button
                                variant="outlined"
                                color="warning"
                                size="small"
                                onClick={() => {
                                    const usuario = window.prompt('Usuário para remover assinatura:');
                                    if (!usuario) return;

                                    const senha = window.prompt('Senha:');
                                    if (!senha) return;

                                    onRemoverAssinatura({ itemId: item.id, usuario, senha });
                                }}
                            >
                                Remover assinatura
                            </Button>
                        )}
                    </Stack>
                ) : possoAssinar ? (
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        onClick={() => {
                            const usuario = window.prompt('Usuário para assinar:');
                            if (!usuario) return;

                            const senha = window.prompt('Senha:');
                            if (!senha) return;

                            onAssinar({ itemId: item.id, usuario, senha });
                        }}
                    >
                        Assinar item
                    </Button>
                ) : (
                    <Typography variant="body2" color="text.secondary">
                        A assinatura será liberada após existir registro do usuário com situação que resolve sua parte.
                    </Typography>
                )}
            </Paper>

            <Divider sx={{ my: 2 }} />

            <Divider sx={{ my: 2 }} />
        </Box>
    );
}

export default HomologationItemDetailPanel;
